import React from 'react';
import { Warning } from '@phosphor-icons/react';
import HelpApiLayout from '../../../../components/layout/HelpApiLayout';
import HelpSidebar from '../../../../components/layout/HelpSidebar';
import ArticleNavigation from '../../../../components/layout/docs/ArticleNavigation';
import LastUpdated from '../../../../components/layout/docs/LastUpdated';
import { getHelpArticleRoute } from '../../../../constants/routes';
import { getHelpRoute } from '../../../../utils/navigation';
import './HelpCategoryPage.css';

/**
 * Secure your Account Article Page
 */
const FirstSplit = () => {
  return (
    <HelpApiLayout type="help" sidebar={<HelpSidebar />}>
      <div className="help-category-page">
        <div className="help-category-container">
          <main className="help-category-main">
            <div className="article-content">
              <header className="article-header">
                <h1 className="article-title">Secure your Account</h1>
                <p className="article-subtitle">
                  Protect your funds and ensure only you have access to your wallet.
                </p>
              </header>

              <div className="article-separator"></div>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Your Secret Recovery Phrase</h2>
                  <p className="article-section-intro">
                    When you create a WeSplit wallet, we generate a unique Secret Recovery Phrase (also known as a Seed Phrase). This is the master key to your funds.
                  </p>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Write it down:</strong> During the setup, write these words down on a piece of paper.
                    </li>
                    <li>
                      <strong>Store it safely:</strong> Keep this paper in a secure location where only you can find it.
                    </li>
                    <li>
                      <strong>Never share it:</strong> WeSplit support will never ask for this phrase. Anyone who has it can access your money.
                    </li>
                  </ol>
                </div>
              </section>


              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Why is this important?</h2>
                  <p className="article-section-intro">
                    Your WeSplit wallet is built on the Solana blockchain. This means:
                  </p>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Total Control:</strong> You can import your WeSplit wallet into any other Solana wallet (like Phantom or Solflare) using this phrase.
                    </li>
                    <li>
                      <strong>Non-Custodial:</strong> You are the only one in control of your assets.
                    </li>
                  </ul>
                </div>
              </section>


              <section className="article-section">
                <div className="article-warning">
                  <div className="article-warning-header">
                    <Warning className="article-warning-icon" weight="fill" />
                    <h3 className="article-warning-title">CRITICAL WARNING</h3>
                  </div>
                  <div className="article-warning-content">
                    <p>
                      If you lose your recovery phrase, you will lose access to your funds permanently.
                    </p>
                    <p>
                      WeSplit does <strong>not</strong> have access to your private keys or recovery phrase. We cannot recover your account or your money if you lose this phrase.
                    </p>
                  </div>
                </div>
              </section>


              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">View your phrase later</h2>
                  <p className="article-section-intro">
                    If you didn't write it down during sign-up, you can still retrieve it as long as you are logged in:
                  </p>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Go to Settings.</strong>
                    </li>
                    <li>
                      <strong>Select Wallet Settings.</strong>
                    </li>
                    <li>
                      <strong>Tap Show Secret Phrase.</strong>
                    </li>
                  </ol>
                </div>
              </section>


              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">App Access Security</h2>
                  <p className="article-section-intro">
                    To ensure no one can open the app if they borrow your phone, we require a security lock.
                  </p>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>6-Digit PIN:</strong> You must set up a 6-digit PIN code to secure the app connection.
                    </li>
                    <li>
                      <strong>FaceID / Biometrics:</strong> For faster access, you can enable FaceID (or TouchID) in the settings. This is optional but recommended.
                    </li>
                  </ul>
                </div>
              </section>

              <div className="article-separator"></div>

              <LastUpdated />

              <ArticleNavigation
                previous={{
                  title: 'Creating an Account',
                  route: getHelpRoute(getHelpArticleRoute('getting-started', 'welcome'))
                }}
                next={{
                  title: 'Top up your account',
                  route: getHelpRoute(getHelpArticleRoute('getting-started', 'top-up-account'))
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default FirstSplit;
